import { type CvssScore } from '../cvss-score';
import { type CvssVectorParser } from '../cvss-vector-parser';
import { getCvssVersion, stripCvssVersionPrefix } from '../utils/cvss-utils';
import { CvssVersion } from '../cvss-enums';
import { Cvss3ScoringEngine } from './cvss3-scoring-engine';
import {
    AttackVector,
    AttackComplexity,
    PrivilegesRequired,
    UserInteraction,
    Scope,
    Impact,
    ExploitCodeMaturity,
    RemediationLevel,
    ReportConfidence,
    SecurityRequirement,
} from './cvss3-enums';

/**
 * Implements a service offering CVSS v3 vector parsing.
 *
 * @remarks
 * Consumers should be aware that {@link parse} will raise an exception if an invalid CVSS v3 vector string is passed.
 * This includes strings containing incorrect keys/values and those that are missing required entries.
 *
 * @public
 */
export class Cvss3VectorParser implements CvssVectorParser {
    /**
     * Converts an attack vector, represented as a string, into an enum value.
     *
     * @param attackVectorString - the string to convert
     * @returns the converted enum value
     */
    private static parseAttackVector(attackVectorString: string): AttackVector {
        switch (attackVectorString) {
            case 'L':
                return AttackVector.Local;
            case 'A':
                return AttackVector.AdjacentNetwork;
            case 'N':
                return AttackVector.Network;
            case 'P':
                return AttackVector.Physical;
        }
        throw new RangeError(
            `Invalid CVSS v3 attack vector value: "${attackVectorString}"`,
        );
    }

    /**
     * Converts an attack complexity, represented as a string, into an enum value.
     *
     * @param attackComplexityString - the string to convert
     * @returns the converted enum value
     */
    private static parseAttackComplexity(
        attackComplexityString: string,
    ): AttackComplexity {
        switch (attackComplexityString) {
            case 'H':
                return AttackComplexity.High;
            case 'L':
                return AttackComplexity.Low;
        }
        throw new RangeError(
            `Invalid CVSS v3 attack complexity value: "${attackComplexityString}"`,
        );
    }

    /**
     * Converts a privileges requirement, represented as a string, into an enum value.
     *
     * @param privilegesRequiredString - the string to convert
     * @returns the converted enum value
     */
    private static parsePrivilegesRequired(
        privilegesRequiredString: string,
    ): PrivilegesRequired {
        switch (privilegesRequiredString) {
            case 'H':
                return PrivilegesRequired.High;
            case 'L':
                return PrivilegesRequired.Low;
            case 'N':
                return PrivilegesRequired.None;
        }
        throw new RangeError(
            `Invalid CVSS v3 privileges required value: "${privilegesRequiredString}"`,
        );
    }

    /**
     * Converts a user interaction level, represented as a string, into an enum value.
     *
     * @param userInteractionString - the string to convert
     * @returns the converted enum value
     */
    private static parseUserInteraction(
        userInteractionString: string,
    ): UserInteraction {
        switch (userInteractionString) {
            case 'R':
                return UserInteraction.Required;
            case 'N':
                return UserInteraction.None;
        }
        throw new RangeError(
            `Invalid CVSS v3 user interaction value: "${userInteractionString}"`,
        );
    }

    /**
     * Converts a scope, represented as a string, into an enum value.
     *
     * @param scopeString - the string to convert
     * @returns the converted enum value
     */
    private static parseScope(scopeString: string): Scope {
        switch (scopeString) {
            case 'C':
                return Scope.Changed;
            case 'U':
                return Scope.Unchanged;
        }
        throw new RangeError(`Invalid CVSS v3 scope value: "${scopeString}"`);
    }

    /**
     * Converts an impact magnitude, represented as a string, into an enum value.
     *
     * @param impactString - the string to convert
     * @returns the converted enum value
     */
    private static parseImpact(impactString: string): Impact {
        switch (impactString) {
            case 'N':
                return Impact.None;
            case 'L':
                return Impact.Low;
            case 'H':
                return Impact.High;
        }
        throw new RangeError(`Invalid CVSS v3 impact value: "${impactString}"`);
    }

    /**
     * Converts an exploit code maturity level, represented as a string, into an enum value.
     *
     * @param exploitCodeMaturityString - the string to convert
     * @returns the converted enum value
     */
    private static parseExploitCodeMaturity(
        exploitCodeMaturityString: string,
    ): ExploitCodeMaturity {
        switch (exploitCodeMaturityString) {
            case 'X':
                return ExploitCodeMaturity.NotDefined;
            case 'U':
                return ExploitCodeMaturity.UnprovenThatExploitsExists;
            case 'P':
                return ExploitCodeMaturity.ProofOfConceptCode;
            case 'F':
                return ExploitCodeMaturity.FunctionalExploitExists;
            case 'H':
                return ExploitCodeMaturity.High;
        }
        throw new RangeError(
            `Invalid CVSS v3 exploit code maturity value: "${exploitCodeMaturityString}"`,
        );
    }

    /**
     * Converts a remediation level, represented as a string, into an enum value.
     *
     * @param remediationLevelString - the string to convert
     * @returns the converted enum value
     */
    private static parseRemediationLevel(
        remediationLevelString: string,
    ): RemediationLevel {
        switch (remediationLevelString) {
            case 'X':
                return RemediationLevel.NotDefined;
            case 'O':
                return RemediationLevel.OfficalFix;
            case 'T':
                return RemediationLevel.TemporaryFix;
            case 'W':
                return RemediationLevel.Workaround;
            case 'U':
                return RemediationLevel.Unavailable;
        }
        throw new RangeError(
            `Invalid CVSS v3 remediation level value: "${remediationLevelString}"`,
        );
    }

    /**
     * Converts a report confidence level, represented as a string, into an enum value.
     *
     * @param reportConfidenceString - the string to convert
     * @returns the converted enum value
     */
    private static parseReportConfidence(
        reportConfidenceString: string,
    ): ReportConfidence {
        switch (reportConfidenceString) {
            case 'X':
                return ReportConfidence.NotDefined;
            case 'U':
                return ReportConfidence.Unknown;
            case 'R':
                return ReportConfidence.Reasonable;
            case 'C':
                return ReportConfidence.Confirmed;
        }
        throw new RangeError(
            `Invalid CVSS v3 report confidence value: "${reportConfidenceString}"`,
        );
    }

    /**
     * Converts a modified attack vector, represented as a string, into an enum value.
     *
     * @param modifiedAttackVectorString - the string to convert
     * @returns the converted enum value
     */
    private static parseModifiedAttackVector(
        modifiedAttackVectorString: string,
    ): AttackVector {
        switch (modifiedAttackVectorString) {
            case 'X':
                return AttackVector.NotDefined;
            case 'L':
                return AttackVector.Local;
            case 'A':
                return AttackVector.AdjacentNetwork;
            case 'N':
                return AttackVector.Network;
            case 'P':
                return AttackVector.Physical;
        }
        throw new RangeError(
            `Invalid CVSS v3 modified attack vector value: "${modifiedAttackVectorString}"`,
        );
    }

    /**
     * Converts a modified attack complexity, represented as a string, into an enum value.
     *
     * @param modifiedAttackComplexityString- the string to convert
     * @returns the converted enum value
     */
    private static parseModifiedAttackComplexity(
        modifiedAttackComplexityString: string,
    ): AttackComplexity {
        switch (modifiedAttackComplexityString) {
            case 'X':
                return AttackComplexity.NotDefined;
            case 'H':
                return AttackComplexity.High;
            case 'L':
                return AttackComplexity.Low;
        }
        throw new RangeError(
            `Invalid CVSS v3 modified attack complexity value: "${modifiedAttackComplexityString}"`,
        );
    }

    /**
     * Converts a modified privileges requirement, represented as a string, into an enum value.
     *
     * @param modifiedPrivilegesRequiredString - the string to convert
     * @returns the converted enum value
     */
    private static parseModifiedPrivilegesRequired(
        modifiedPrivilegesRequiredString: string,
    ): PrivilegesRequired {
        switch (modifiedPrivilegesRequiredString) {
            case 'X':
                return PrivilegesRequired.NotDefined;
            case 'H':
                return PrivilegesRequired.High;
            case 'L':
                return PrivilegesRequired.Low;
            case 'N':
                return PrivilegesRequired.None;
        }
        throw new RangeError(
            `Invalid CVSS v3 modified privileges required value: "${modifiedPrivilegesRequiredString}"`,
        );
    }

    /**
     * Converts a modified user interaction level, represented as a string, into an enum value.
     *
     * @param modifiedUserInteractionString - the string to convert
     * @returns the converted enum value
     */
    private static parseModifiedUserInteraction(
        modifiedUserInteractionString: string,
    ): UserInteraction {
        switch (modifiedUserInteractionString) {
            case 'X':
                return UserInteraction.NotDefined;
            case 'R':
                return UserInteraction.Required;
            case 'N':
                return UserInteraction.None;
        }
        throw new RangeError(
            `Invalid CVSS v3 modified user interaction value: "${modifiedUserInteractionString}"`,
        );
    }

    /**
     * Converts a modified scope, represented as a string, into an enum value.
     *
     * @param modifiedScopeString- the string to convert
     * @returns the converted enum value
     */
    private static parseModifiedScope(modifiedScopeString: string): Scope {
        switch (modifiedScopeString) {
            case 'X':
                return Scope.NotDefined;
            case 'C':
                return Scope.Changed;
            case 'U':
                return Scope.Unchanged;
        }
        throw new RangeError(
            `Invalid CVSS v3 modified scope value: "${modifiedScopeString}"`,
        );
    }

    /**
     * Converts a modified impact magnitude, represented as a string, into an enum value.
     *
     * @param modifiedImpactString- the string to convert
     * @returns the converted enum value
     */
    private static parseModifiedImpact(modifiedImpactString: string): Impact {
        switch (modifiedImpactString) {
            case 'X':
                return Impact.NotDefined;
            case 'N':
                return Impact.None;
            case 'L':
                return Impact.Low;
            case 'H':
                return Impact.High;
        }
        throw new RangeError(
            `Invalid CVSS v3 modified impact value: "${modifiedImpactString}"`,
        );
    }

    /**
     * Converts a security requirement, represented as a string, into an enum value.
     *
     * @param securityRequirementString - the string to convert
     * @returns the converted enum value
     */
    private static parseSecurityRequirement(
        securityRequirementString: string,
    ): SecurityRequirement {
        switch (securityRequirementString) {
            case 'X':
                return SecurityRequirement.NotDefined;
            case 'L':
                return SecurityRequirement.Low;
            case 'M':
                return SecurityRequirement.Medium;
            case 'H':
                return SecurityRequirement.High;
        }
        throw new RangeError(
            `Invalid CVSS v3 security requirement value: "${securityRequirementString}"`,
        );
    }

    /**
     * Generates and returns a version-specific (CVSS v3.x) scoring engine loaded with a vector.
     *
     * @param vector - the vector to load in to the scoring engine
     * @returns the loaded scoring engine
     */
    public generateScoringEngine(vector: string): Cvss3ScoringEngine {
        // Variable to hold vector with version prefix stripped. Strip brackets if present.
        const strippedVector = stripCvssVersionPrefix(vector);

        // Ensure that the vector is a valid CVSS v2 vector.
        const version = getCvssVersion(vector);
        if (![CvssVersion.CVSS30, CvssVersion.CVSS31].includes(version)) {
            throw new RangeError(
                `Invalid CVSS version: "${version}". Expected CVSS v3.0 or v3.1.`,
            );
        }

        // Get scoring engine ready.
        const cvss = new Cvss3ScoringEngine(version);

        // Split along slashes.
        const segments = strippedVector.split('/');
        for (const segment of segments) {
            // Split segment.
            const sections = segment.split(':');

            // Ensure that the segment is valid.
            const metric = sections[0];
            const value = sections[1];

            if (!metric || !value) {
                throw new RangeError(
                    `Invalid CVSS v3 vector segment: "${segment}"`,
                );
            }

            // Parse segment.
            switch (metric.toUpperCase()) {
                case 'AV':
                    cvss.attackVector =
                        Cvss3VectorParser.parseAttackVector(value);
                    break;
                case 'AC':
                    cvss.attackComplexity =
                        Cvss3VectorParser.parseAttackComplexity(value);
                    break;
                case 'PR':
                    cvss.privilegesRequired =
                        Cvss3VectorParser.parsePrivilegesRequired(value);
                    break;
                case 'UI':
                    cvss.userInteraction =
                        Cvss3VectorParser.parseUserInteraction(value);
                    break;
                case 'S':
                    cvss.scope = Cvss3VectorParser.parseScope(value);
                    break;
                case 'C':
                    cvss.confidentialityImpact =
                        Cvss3VectorParser.parseImpact(value);
                    break;
                case 'I':
                    cvss.integrityImpact = Cvss3VectorParser.parseImpact(value);
                    break;
                case 'A':
                    cvss.availabilityImpact =
                        Cvss3VectorParser.parseImpact(value);
                    break;
                case 'E':
                    cvss.exploitCodeMaturity =
                        Cvss3VectorParser.parseExploitCodeMaturity(value);
                    break;
                case 'RL':
                    cvss.remediationLevel =
                        Cvss3VectorParser.parseRemediationLevel(value);
                    break;
                case 'RC':
                    cvss.reportConfidence =
                        Cvss3VectorParser.parseReportConfidence(value);
                    break;
                case 'MAV':
                    cvss.modifiedAttackVector =
                        Cvss3VectorParser.parseModifiedAttackVector(value);
                    break;
                case 'MAC':
                    cvss.modifiedAttackComplexity =
                        Cvss3VectorParser.parseModifiedAttackComplexity(value);
                    break;
                case 'MPR':
                    cvss.modifiedPrivilegesRequired =
                        Cvss3VectorParser.parseModifiedPrivilegesRequired(
                            value,
                        );
                    break;
                case 'MUI':
                    cvss.modifiedUserInteraction =
                        Cvss3VectorParser.parseModifiedUserInteraction(value);
                    break;
                case 'MS':
                    cvss.modifiedScope =
                        Cvss3VectorParser.parseModifiedScope(value);
                    break;
                case 'MC':
                    cvss.modifiedConfidentialityImpact =
                        Cvss3VectorParser.parseModifiedImpact(value);
                    break;
                case 'MI':
                    cvss.modifiedIntegrityImpact =
                        Cvss3VectorParser.parseModifiedImpact(value);
                    break;
                case 'MA':
                    cvss.modifiedAvailabilityImpact =
                        Cvss3VectorParser.parseModifiedImpact(value);
                    break;
                case 'CR':
                    cvss.confidentialityRequirement =
                        Cvss3VectorParser.parseSecurityRequirement(value);
                    break;
                case 'IR':
                    cvss.integrityRequirement =
                        Cvss3VectorParser.parseSecurityRequirement(value);
                    break;
                case 'AR':
                    cvss.availabilityRequirement =
                        Cvss3VectorParser.parseSecurityRequirement(value);
                    break;
                default:
                    throw new RangeError(
                        `Invalid CVSS v3 vector key: "${value}"`,
                    );
            }
        }

        // Return scoring engine.
        return cvss;
    }

    /**
     * {@inheritDoc}
     */
    public parse(vector: string): CvssScore {
        // Return computed score.
        return this.generateScoringEngine(vector).computeScore();
    }

    // TODO[CVSS]: Make sure that vector has proper order
    public reorderVector(vector: string): string {
        const version = getCvssVersion(vector);
        if (![CvssVersion.CVSS30, CvssVersion.CVSS31].includes(version)) {
            throw new RangeError(
                `Invalid CVSS version: "${version}". Expected CVSS v3.0 or v3.1.`,
            );
        }
        return vector;
    }
}
